import type { Item } from '@cheqroom/api';
import { useMemo } from 'react';

import { createCheckoutPermissions } from './use-checkout-permissions.hook';
import { type Props as PermissionsProps, usePermissions } from './use-permissions.hook';
import { createReservationPermissions } from './use-reservation-permissions.hook';
import {
	canAdminItem,
	canGiveItemCustody,
	canReleaseItemCustody,
	canTakeItemCustody,
	canTransferItemCustody,
} from './utils/item';
import { canAdminKit } from './utils/kit';

export const createItemPermissions = (permissions: ReturnType<typeof usePermissions>) => {
	const { hasAnyPermission, useImport, useExport, isOwn, useReleaseAtLocation, useFlags, useDepreciations } =
		permissions;

	const { canReserve } = createReservationPermissions(permissions);
	const { canCheckout } = createCheckoutPermissions(permissions);

	const canAdmin = () => canAdminItem(permissions);

	const itemPermissions = {
		canRead: () => hasAnyPermission('ITEMS_READER', 'ITEMS_READER_RESTRICTED'),
		canCreate: canAdmin,
		canUpdate: canAdmin,
		canChangeLocation: canAdmin,
		canChangeCategory: canAdmin,
		canImport: () => useImport && hasAnyPermission('ITEMS_IMPORTER', 'ITEMS_IMPORTER_RESTRICTED'),
		canExport: () => useExport && hasAnyPermission('ITEMS_EXPORTER', 'ITEMS_EXPORTER_RESTRICTED'),
		canReserve: (item?: Pick<Item, 'canReserve'>) => {
			if (!canReserve(true)) return false;
			return item ? item?.canReserve === 'available' : true;
		},
		canCheckout: (item?: Pick<Item, 'canOrder'>) => {
			if (!canCheckout(true)) return false;
			return item ? item?.canOrder === 'available' : true;
		},
		canRetire: (item?: Pick<Item, 'status'>) => {
			if (!canAdmin()) return false;
			return item ? item?.status !== 'expired' : true;
		},
		canUnretire: (item?: Pick<Item, 'status'>) => {
			if (!canAdmin()) return false;
			return item ? item?.status === 'expired' : true;
		},
		canDelete: (item?: Pick<Item, 'status'>) => {
			if (!canAdmin()) return false;
			return item ? item?.status === 'available' || item?.status === 'expired' : true;
		},

		canGoToCheckout: (item: Pick<Item, 'status' | 'kind'>) => {
			return (item?.status === 'available' || item?.status === 'expired') && item.kind === 'simple';
		},

		canDuplicate: canAdmin,

		canAddToKit: (item?: Pick<Item, 'kit'>) => {
			if (!canAdminKit(permissions)) return false;
			return item ? !item?.kit : true;
		},
		canUpdateKitQuantity: (item?: Pick<Item, 'kit' | 'inKits'>) => {
			if (!canAdminKit(permissions)) return false;
			return item ? !!item?.kit || !!item?.inKits : true;
		},
		canRemoveFromKit: (item?: Pick<Item, 'kit' | 'inKits'>) => {
			if (!canAdminKit(permissions)) return false;
			return item ? !!item?.kit || !!item?.inKits : true;
		},

		canTakeCustody: (item?: Pick<Item, 'canCustody'> & { custody: string }) => {
			return canTakeItemCustody(permissions) && (item ? item?.canCustody === 'available' : true);
		},
		canTransferCustody: (own?: boolean) => canTransferItemCustody(permissions, own),
		canGiveCustody: (item?: Pick<Item, 'canCustody'> & { custody: string }) => {
			const own = item ? isOwn(item?.custody) : false;
			return canGiveItemCustody(permissions, own);
		},
		canReleaseCustody: (item?: Pick<Item, 'canCustody'> & { custody: string }) => {
			const own = item ? isOwn(item?.custody) : false;
			return canReleaseItemCustody(permissions, own);
		},
		canReleaseCustodyAt: (item?: Pick<Item, 'canCustody'> & { custody: string }) => {
			return itemPermissions.canReleaseCustody(item) && useReleaseAtLocation;
		},
		canChangeFlag: () => {
			return useFlags && hasAnyPermission('ITEMS_FLAGGER', 'ITEMS_FLAGGER_RESTRICTED');
		},
		canClearFlag: () => {
			return useFlags && hasAnyPermission('ITEMS_UNFLAGGER', 'ITEMS_UNFLAGGER_RESTRICTED');
		},
		canChangeCover: canAdmin,
		canUpdatePermissions: canAdmin,
		canAddAttachment: () => {
			return hasAnyPermission('ITEMS_ATTACHMENTS_OWN_WRITER');
		},
		canRemoveAttachment: (isOwn = false) => {
			return (
				hasAnyPermission('ITEMS_ATTACHMENTS_DELETER') ||
				(isOwn && hasAnyPermission('ITEMS_ATTACHMENTS_OWN_DELETER'))
			);
		},
		canAddComment: () => {
			return hasAnyPermission('ITEMS_COMMENTS_OWN_WRITER');
		},
		canEditComment: (isOwnComment = false) => {
			return isOwnComment && hasAnyPermission('ITEMS_COMMENTS_OWN_WRITER');
		},
		canRemoveComment: (isOwnComment = false) => {
			return (
				hasAnyPermission('ITEMS_COMMENTS_DELETER') ||
				(isOwnComment && hasAnyPermission('ITEMS_COMMENTS_OWN_DELETER'))
			);
		},
		canUpdateGeo: canAdmin,
		canClearCodes: canAdmin,
		canGenerateCodes: canAdmin,
		canViewItemDepreciation: () => useDepreciations && canAdmin(),
	};

	return itemPermissions;
};

export const useItemPermissions = (props: PermissionsProps) => {
	const permissions = usePermissions(props);

	return useMemo(() => createItemPermissions(permissions), [permissions]);
};
